import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useState } from "react";
import {
  LayoutDashboard, Calendar, Users, DollarSign, Menu as MenuIcon,
  Plus, Sparkles, LogOut, Calculator, Scissors, MessageSquare,
  Package, Link2, Settings, ChevronLeft, UserX, Cake, ClipboardList
} from "lucide-react";

const bottomNav = [
  { to: "/app", icon: LayoutDashboard, label: "Início", end: true, testid: "nav-home" },
  { to: "/app/agenda", icon: Calendar, label: "Agenda", testid: "nav-agenda" },
  { to: "/app/clientes", icon: Users, label: "Clientes", testid: "nav-clients" },
  { to: "/app/financeiro", icon: DollarSign, label: "Financeiro", testid: "nav-finance" },
  { to: "/app/mais", icon: MenuIcon, label: "Mais", testid: "nav-more" },
];

const desktopExtras = [
  { to: "/app/servicos", icon: Scissors, label: "Serviços", testid: "nav-services" },
  { to: "/app/precificacao", icon: Calculator, label: "Precificação", testid: "nav-pricing" },
  { to: "/app/mensagens", icon: MessageSquare, label: "Mensagens", testid: "nav-messages" },
  { to: "/app/reativacao", icon: UserX, label: "Reativação", testid: "nav-reactivation" },
  { to: "/app/aniversarios", icon: Cake, label: "Aniversários", testid: "nav-birthdays" },
  { to: "/app/anamnese", icon: ClipboardList, label: "Anamnese", testid: "nav-anamnesis" },
  { to: "/app/configuracoes", icon: Settings, label: "Configurações", testid: "nav-settings" },
];

export default function AppLayout() {
  const { user, studio, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [fabOpen, setFabOpen] = useState(false);

  const handleLogout = async () => { await logout(); nav("/login"); };

  const showFabMenu = () => setFabOpen((v) => !v);
  const doAction = (path) => { setFabOpen(false); nav(path); };

  return (
    <div className="min-h-screen bg-[#FAF8F6]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 bottom-0 w-64 flex-col bg-white border-r border-[#E4E4E7]">
        <div className="p-5 border-b border-[#E4E4E7]">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-[#682F3E] flex items-center justify-center">
              <Sparkles size={18} className="text-[#EBD8D2]" strokeWidth={1.8} />
            </div>
            <div>
              <div className="font-display font-bold text-[#27272A] leading-none">NailControl</div>
              <div className="text-[11px] text-[#71717A] mt-0.5">{studio?.name || "Studio"}</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {[...bottomNav.slice(0, 4), ...desktopExtras].map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} data-testid={`side-${item.testid}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition ${
                  isActive ? "bg-[#F5ECE8] text-[#682F3E]" : "text-[#71717A] hover:bg-[#FAF8F6] hover:text-[#27272A]"
                }`
              }>
              <item.icon size={18} strokeWidth={1.8} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[#E4E4E7]">
          <div className="flex items-center gap-2 px-2 py-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-[#EBD8D2] flex items-center justify-center text-[#682F3E] text-xs font-bold">
              {(user?.display_name || user?.name || "U").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-[#27272A] truncate">{user?.display_name || user?.name}</div>
              <div className="text-xs text-[#71717A] truncate">{user?.email}</div>
            </div>
          </div>
          <button data-testid="btn-logout" onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-[#71717A] hover:bg-[#FAF8F6] hover:text-[#B76E56]">
            <LogOut size={16} /> Sair
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[#E4E4E7] px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#682F3E] flex items-center justify-center">
            <Sparkles size={16} className="text-[#EBD8D2]" strokeWidth={1.8} />
          </div>
          <div>
            <div className="font-display font-bold text-[#27272A] text-sm leading-none">NailControl</div>
            <div className="text-[10px] text-[#71717A] mt-0.5 truncate max-w-[160px]">{studio?.name || ""}</div>
          </div>
        </div>
        <button data-testid="mobile-logout" onClick={handleLogout} className="text-[#71717A] p-1">
          <LogOut size={18} />
        </button>
      </header>

      {/* Main */}
      <main className="lg:ml-64 pb-24 lg:pb-6">
        <div className="max-w-5xl mx-auto px-4 lg:px-8 py-4 lg:py-8">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav data-testid="bottom-nav" className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-lg border-t border-[#E4E4E7] px-2 py-2 flex justify-around items-center">
        {bottomNav.map(item => (
          <NavLink key={item.to} to={item.to} end={item.end} data-testid={item.testid}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center gap-0.5 py-1.5 text-[10px] font-medium ${isActive ? "text-[#682F3E]" : "text-[#71717A]"}`
            }>
            <item.icon size={20} strokeWidth={1.8} />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* FAB */}
      <div className="fixed bottom-20 right-4 lg:bottom-6 lg:right-6 z-40 flex flex-col items-end gap-2">
        {fabOpen && (
          <div className="flex flex-col items-end gap-2 mb-2" data-testid="fab-menu">
            <FabAction label="Novo agendamento" onClick={() => doAction("/app/agenda?new=1")} tid="fab-new-appt" />
            <FabAction label="Nova cliente" onClick={() => doAction("/app/clientes?new=1")} tid="fab-new-client" />
            <FabAction label="Nova receita" onClick={() => doAction("/app/financeiro?new=receita")} tid="fab-new-income" />
            <FabAction label="Nova despesa" onClick={() => doAction("/app/financeiro?new=despesa")} tid="fab-new-expense" />
            <FabAction label="Calcular preço" onClick={() => doAction("/app/precificacao")} tid="fab-new-pricing" />
          </div>
        )}
        <button data-testid="fab-toggle" onClick={showFabMenu}
          className="w-14 h-14 rounded-full bg-[#682F3E] hover:bg-[#542431] text-white flex items-center justify-center shadow-[0_8px_30px_rgba(104,47,62,0.35)] transition-transform hover:scale-105">
          <Plus size={26} strokeWidth={2.2} className={`transition-transform ${fabOpen ? "rotate-45" : ""}`} />
        </button>
      </div>
    </div>
  );
}

function FabAction({ label, onClick, tid }) {
  return (
    <button data-testid={tid} onClick={onClick}
      className="bg-white text-[#27272A] px-4 py-2 rounded-full text-sm font-semibold shadow-md border border-[#E4E4E7] hover:border-[#C98F96]">
      {label}
    </button>
  );
}
